import React from 'react';
import { Database, Brain, UserCheck, TrendingUp, Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { mockData } from '../mock';

const iconMap = {
  Database: Database,
  Brain: Brain,
  UserCheck: UserCheck,
  TrendingUp: TrendingUp
};

const HowItWorks = () => {
  const { howItWorks } = mockData;

  const stepImages = [
    "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=600&q=80",
    "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=600&q=80",
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80",
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80"
  ];

  return (
    <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-blue-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-100 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold">
            Our Methodology
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-6 mb-4">
            How GaaS Trust Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Systematic hybrid AI-human platform delivering proven results through four strategic phases
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-2 gap-8">
          {howItWorks.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <Card
                key={step.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200 cursor-pointer relative overflow-hidden"
              >
                {/* Background Image */}
                <div className="absolute top-0 right-0 w-40 h-40 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                  <img 
                    src={stepImages[index]} 
                    alt={step.title}
                    className="w-full h-full object-cover rounded-bl-full"
                  />
                </div>

                {/* Step Number Badge */}
                <div className="absolute top-4 right-4 w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300 z-10">
                  <span className="text-2xl font-bold text-blue-600 group-hover:text-white transition-colors duration-300">
                    {step.step}
                  </span>
                </div>

                <CardHeader>
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {step.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed text-base">
                    {step.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {step.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;